"use client";

import { useLocale, useTranslations } from "next-intl";
import { useState, useTransition } from "react";
import { locales, localeLabels, type Locale } from "@/i18n/config";
import { setLocaleAction } from "@/i18n/actions";
import { cn } from "@/lib/utils";

export function LanguageSwitcher({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const locale = useLocale() as Locale;
  const t = useTranslations("common");
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  function choose(next: Locale) {
    setOpen(false);
    startTransition(() => setLocaleAction(next));
  }

  if (variant === "mobile") {
    return (
      <div className="flex flex-wrap gap-1.5 px-3 py-2">
        {locales.map((l) => (
          <button
            key={l}
            type="button"
            disabled={isPending}
            onClick={() => choose(l)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-xs font-medium",
              l === locale ? "border-gold bg-gold text-ink" : "border-ink-border text-ivory-muted"
            )}
          >
            {localeLabels[l]}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        title={t("language")}
        className="flex h-9 items-center gap-1.5 rounded-full border border-ink-border px-3 text-xs font-semibold text-ivory-muted hover:text-ivory disabled:opacity-50"
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.7 4 6 4 9s-1.5 6.3-4 9c-2.5-2.7-4-6-4-9s1.5-6.3 4-9z" />
        </svg>
        {localeLabels[locale]}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-36 overflow-hidden rounded-2xl border border-ink-border bg-ink-raised-2 py-1.5 shadow-2xl">
          {locales.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => choose(l)}
              className={cn(
                "block w-full px-4 py-2 text-left text-sm",
                l === locale ? "text-gold-soft" : "text-ivory hover:bg-surface"
              )}
            >
              {localeLabels[l]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
